import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import FroggySpinner from '../components/common/FroggySpinner';
import { getHomepageData, getProducts } from '../services/api';
import ProductCard from '../components/Products/ProductCard';
import SkeletonCardGrid from '../components/common/SkeletonCardGrid';
import SpeciesSpotlight from '../components/common/SpeciesSpotlight';
import ArrivalNotifyForm from '../components/common/ArrivalNotifyForm';
import SEO from '../components/SEO';
import { openFroggyChat } from '../components/FroggyChatBubble/FroggyChatBubble';
import { FULL_KEYWORDS, ORGANIZATION_JSONLD, WEBSITE_JSONLD } from '../constants/seoKeywords';
import './HomePage.css';

const HomePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [homepageData, setHomepageData] = useState(null);
  // Animals-first restructure (Gap 4, 2026-08-14): "Currently Available"
  // rail directly under the hero. Fetched in parallel with the homepage
  // config; a rail failure must never take the homepage down, so it has
  // its own state and degrades to just the Shop-All CTA.
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(true);

  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        setLoading(true);
        const data = await getHomepageData();
        setHomepageData(data);
        setError(null);
      } catch (err) {
        console.error('[HomePage] Error fetching data:', err);
        setError(err.response?.data?.error || 'Failed to load homepage');
      } finally {
        setLoading(false);
      }
    };

    fetchHomepageData();
  }, []);

  useEffect(() => {
    const fetchAvailable = async () => {
      try {
        setAvailableLoading(true);
        // Server orders marketing-featured animals first (the Koi pair /
        // signature inventory), then newest arrivals; limit caps payload.
        const products = await getProducts({ isForSale: true, featuredFirst: true, limit: 6 });
        // slice() backstop: a backend that predates the limit param
        // ignores it and returns the full catalog.
        setAvailableProducts(Array.isArray(products) ? products.slice(0, 6) : []);
      } catch (err) {
        // Non-fatal: rail hides, CTA to /frogs remains.
        console.warn('[HomePage] Currently Available rail failed:', err?.message || err);
        setAvailableProducts([]);
      } finally {
        setAvailableLoading(false);
      }
    };

    fetchAvailable();
  }, []);

  if (loading) {
    return (
      <Container className="text-center py-5">
        <FroggySpinner size="lg" />
        <p className="mt-3">Loading homepage...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Unable to Load Homepage</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  const { store, featured_promotions, featured_section, seo_intro } = homepageData || {};

  // StoreConfig field mapping
  const hasHeroImage = Boolean(store?.hero_image_url);
  const headerTagline = store?.tagline; // "Feeling Froggy LLC"
  const headerTitle = store?.home_intro_title || store?.facility_name || 'Feeling Froggy'; // Main H1
  const introExcerpt = store?.home_intro_excerpt; // Bombina description below hero image

  // Featured deals section content
  const featuredTitle = featured_section?.title || 'Featured Deals';
  const featuredSubtitle = featured_section?.subtitle || '';

  // #103 MINIMAL — zero-inventory graceful degradation (operator
  // strategic direction: at zero available animals the homepage must
  // read as an info destination, not an empty shop). The empty
  // Currently-Available rail becomes a content-first block (care-guides
  // entry + a quiet arrivals line) and the existing FroggyAI callout is
  // PROMOTED to render directly under it instead of further down the
  // page. Inventory weeks are byte-identical to today; the lean-week
  // deals step-up (<3 products) below is untouched.
  const zeroInventory = !availableLoading && availableProducts.length === 0;
  // #103 full tier — lean week (<3 products, matching the deals step-up
  // gate below): the arrivals email capture renders small under the rail.
  // Full-inventory weeks show NO capture at all.
  const leanInventory = !availableLoading
    && availableProducts.length > 0 && availableProducts.length < 3;

  // The one FroggyAI callout, rendered in ONE of two slots (promoted at
  // zero inventory, its usual spot otherwise) — extracted so the two
  // slots cannot drift. (P-09a-6b, 2026-05-30; demoted 2026-08-14;
  // re-promoted at zero inventory only, #103 2026-08-17.) The whole card
  // OPENS the chat, exactly as the pill does (operator, 2026-10-09 —
  // reversing the earlier text-only choice).
  const froggyCallout = (
    <section
      className="home-froggy-callout home-froggy-callout--compact home-froggy-callout--opens"
      aria-labelledby="home-froggy-callout-title"
      role="button"
      tabIndex={0}
      onClick={openFroggyChat}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFroggyChat(); }
      }}
    >
      <img
        src="/logos/ff_original_96px.png"
        alt=""
        aria-hidden="true"
        className="home-froggy-callout-logo"
      />
      <div className="home-froggy-callout-text">
        <h2 id="home-froggy-callout-title" className="home-froggy-callout-title">
          Meet FroggyAI
        </h2>
        <p className="home-froggy-callout-body">
          Have a quick question about amphibian care? Ask here, or{' '}
          <strong>Ask FroggyAI</strong> at the bottom right &mdash; free,
          expert advice from our AI assistant. No signup needed.
        </p>
      </div>
    </section>
  );

  return (
    <>
      <SEO
        title="Large Obligate Dart Frogs & Select Geckos — Feeling Froggy"
        description={
          store?.home_intro_excerpt
            ? store.home_intro_excerpt.substring(0, 155) + (store.home_intro_excerpt.length > 155 ? '...' : '')
            : 'Specialty breeder of large obligate dart frogs (Oophaga) and select geckos — leachianus, Black Night & Mandarin leopard geckos. Captive-bred, live-arrival guaranteed.'
        }
        canonical="https://feelingfroggy.llc/"
        keywords={FULL_KEYWORDS}
        jsonLd={[ORGANIZATION_JSONLD, WEBSITE_JSONLD]}
        ogImage={store?.hero_image_url || 'https://feelingfroggy.llc/logos/ff_original_96px.png'}
      />

      {/* Page Header - Above hero image */}
      <header className="home-page-header">
        {headerTagline && (
          <p className="home-header-tagline">{headerTagline}</p>
        )}
        <h1 className="home-header-title">{headerTitle}</h1>
      </header>

      {/* Hero Image - No overlay text */}
      <div
        className="homepage-hero"
        style={{
          backgroundImage: hasHeroImage ? `url(${store.hero_image_url})` : 'none',
          backgroundColor: hasHeroImage ? 'transparent' : 'var(--ff-brandBlue)'
        }}
      />

      {/* Hero Content - Below the image */}
      <section className="home-hero-content">
        {introExcerpt && (
          <p className="home-tagline">{introExcerpt}</p>
        )}
      </section>

      {/* Spacer After Hero */}
      <div className="home-after-hero-spacer" />

      {/* Currently Available — real animals directly under the hero
          (animals-first restructure, competitive-benchmark Gap 4).
          Marketing-featured animals lead, then newest arrivals. Cards are
          the same ProductCard the category pages use, so photos, pricing
          (incl. lot/individual ranges) and links stay consistent. */}
      <Container className="homepage-available-container" aria-label="Currently available animals">
        {availableLoading ? (
          <>
            <h2 id="homepage-available-title" className="homepage-available-title">Currently Available</h2>
            <SkeletonCardGrid count={3} />
          </>
        ) : availableProducts.length > 0 ? (
          <>
            <h2 id="homepage-available-title" className="homepage-available-title">Currently Available</h2>
            <p className="homepage-available-subtitle">
              Captive-bred animals ready to ship now
            </p>
            <Row className="g-4">
              {availableProducts.map((product) => (
                <Col key={product.id} xs={12} sm={6} md={4}>
                  {/* eagerImage: the rail sits directly under the hero —
                      photos should request immediately (#99) */}
                  <ProductCard product={product} compact hideCareGuideLink eagerImage />
                </Col>
              ))}
            </Row>
          </>
        ) : (
          /* #103 MINIMAL — content-first block instead of an empty rail:
             the site reads as an info destination while inventory is dry.
             Care guides lead (the info core), the shop CTA below stays as
             the quiet secondary path. */
          <div className="homepage-zero-hero">
            <h2 id="homepage-available-title" className="homepage-available-title">
              Start With the Care Guides
            </h2>
            <p className="homepage-available-subtitle">
              Species-by-species husbandry, from our racks to yours
            </p>
            <div className="homepage-zero-hero-cta">
              <Link to="/care-guides" className="ff-btn ff-btn--primary">
                Browse Care Guides
              </Link>
            </div>
            {/* #103 full tier — the Species Spotlight slots into the
                zero-hero (it renders near the FroggyAI callout in
                inventory weeks instead; see below). */}
            <SpeciesSpotlight />
            <p className="homepage-zero-hero-note">
              New arrivals are on the way &mdash; check back soon.
            </p>
            {/* #103 full tier — anonymous arrivals capture, prominent in
                the zero-inventory hero. */}
            <ArrivalNotifyForm className="homepage-zero-hero-notify" />
          </div>
        )}
        {/* #103 full tier — lean weeks only (<3 products): the same
            capture, subtle and compact, under the thin rail. Full weeks
            render nothing here. */}
        {leanInventory && (
          <ArrivalNotifyForm compact className="homepage-lean-notify" />
        )}
        <div className="homepage-cta-wrapper">
          {/* Design System v1 §2.2 (Phase 3): the home page's ONE primary —
              except in a zero-inventory week, when Browse Care Guides is
              the hero's primary and this steps down to a secondary. */}
          <Link
            to="/frogs"
            className={`ff-btn ${zeroInventory ? 'ff-btn--secondary' : 'ff-btn--primary'} ff-shop-btn`}
          >
            Shop All Available Frogs
          </Link>
        </div>
      </Container>

      {/* #103 — promoted FroggyAI callout slot (zero inventory only) */}
      {zeroInventory && froggyCallout}

      {/* SitePage SEO Intro Section (optional, separate from StoreConfig) */}
      {seo_intro && (
        <Container className="py-5">
          <Row className="mb-5">
            <Col lg={10} className="mx-auto">
              {seo_intro.title && (
                <h2 className="text-center mb-4">{seo_intro.title}</h2>
              )}
              {seo_intro.content && (
                <div className="homepage-intro-content">
                  {seo_intro.content}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      )}

      {/* FroggyAI awareness callout — usual slot: above the deals strip
          (operator 2026-08-14: FroggyAI is new — it outranks the coupons).
          At zero inventory it renders in the promoted slot above instead
          (#103); JSX extracted to `froggyCallout` so the two slots cannot
          drift. */}
      {!zeroInventory && froggyCallout}

      {/* #103 full tier — Species Spotlight renders in EVERY inventory
          tier (it's content). Inventory weeks: here, beside the FroggyAI
          callout. Zero inventory: inside the zero-hero block above.
          Gated on !availableLoading so it doesn't jump slots mid-load. */}
      {!availableLoading && !zeroInventory && <SpeciesSpotlight />}

      {/* Featured Deals — demoted to a quiet strip below the animals rail
          (Gap 4). However many promos are live, same /promo/<slug> links,
          compact horizontal cards instead of full-height hero cards. */}
      {featured_promotions && featured_promotions.length > 0 && (
              <Container className={`homepage-deals-container${!availableLoading && availableProducts.length < 3 ? ' homepage-deals-container--primary' : ''}`} aria-labelledby="homepage-deals-title">
          <div className="homepage-deals-strip">
            <h2 id="homepage-deals-title" className="homepage-deals-title">{featuredTitle}</h2>
            {featuredSubtitle && (
              <p className="homepage-deals-subtitle">{featuredSubtitle}</p>
            )}
            {/* Operator 2026-08-21: the grid was hardcoded to thirds, so a
                single live promo rendered at a third of the width and read
                as stranded. Width now follows the count — one takes a half,
                two take halves, three or more take thirds. Centring is
                UNCONDITIONAL: on a full row it is provably inert (3x33.3%
                or 2x50% leaves no free space, and every xs column is
                col-12), so it only acts on a partial row — which also
                catches the 4th/5th/7th promo that would otherwise strand
                on row two. */}
            <Row className="g-3 justify-content-center">
              {featured_promotions.map((promo) => {
                const dealBody = (
                  <>
                    {promo.hero_image_url && (
                      <img
                        loading="lazy"
                        src={promo.hero_image_url}
                        alt={`${promo.customer_facing_title || promo.name} - ${promo.short_description || 'Special promotion'}`}
                        className="homepage-deal-card-img"
                      />
                    )}
                    <div className="homepage-deal-card-body">
                      <div className="homepage-deal-card-title">
                        {promo.customer_facing_title || promo.name}
                      </div>
                      {promo.short_description && (
                        <div className="homepage-deal-card-desc">{promo.short_description}</div>
                      )}
                      {promo.code && (
                        <span className="promo-code-pill homepage-deal-card-code">
                          CODE: {promo.code}
                        </span>
                      )}
                    </div>
                  </>
                );
                return (
                  <Col
                    key={promo.id}
                    xs={12}
                    md={featured_promotions.length >= 3 ? 4 : 6}
                  >
                    {promo.slug ? (
                      <Link to={`/promo/${promo.slug}`} className="homepage-deal-card">
                        {dealBody}
                      </Link>
                    ) : (
                      <div className="homepage-deal-card">{dealBody}</div>
                    )}
                  </Col>
                );
              })}
            </Row>
          </div>
        </Container>
      )}


    </>
  );
};

export default HomePage;
